import {Injectable, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {TenantService} from '@services/core/tenant/tenant.service';
import {AppointmentListItem} from '@models/data/appointments/AppointmentListItem';
import {AppointmentStatus} from '@app/shared/models/data/appointments/enums/AppointmentStatus.enum';
import {Queue} from '@shared/common/Queue';
import {hasValue} from '@shared/utils/helpers';
import {DateUtil} from '@shared/utils/dates/DateParsing.util';
import {environment} from '@environments/environment';
import {AppointmentType} from '@models/data/appointment-types/AppointmentType';
import {ParentCommsService} from '@services/core/comms/parent-comms.service';
import {AppointmentUtil} from '@models/data/appointments/Appointment';


export class ConsultationPopupRibbonServiceObservables {
  private readonly TAG = 'ConsultationPopupRibbonServiceObservables';

  public shouldShowPopup$: Observable<boolean>;
  public loadingNextConsult$: Observable<boolean>;
  public queue$: Observable<Queue<AppointmentListItem>>;
  public displayNextAppointments$: Observable<AppointmentListItem[]>;
  public activeAppointmentItem$: Observable<AppointmentListItem>;

  private shouldShowPopupSubject: BehaviorSubject<boolean>;
  private loadingNextConsultSubject: BehaviorSubject<boolean>;
  private queueSubject: BehaviorSubject<Queue<AppointmentListItem>>;
  private displayNextAppointmentsSubject: BehaviorSubject<AppointmentListItem[]>;
  private activeAppointmentItemSubject: BehaviorSubject<AppointmentListItem>;

  constructor() {
    this.shouldShowPopupSubject = new BehaviorSubject<boolean>(false);
    this.shouldShowPopup$ = this.shouldShowPopupSubject.asObservable();

    this.loadingNextConsultSubject = new BehaviorSubject<boolean>(false);
    this.loadingNextConsult$ = this.loadingNextConsultSubject.asObservable();

    this.queueSubject = new BehaviorSubject<Queue<AppointmentListItem>>(new Queue<AppointmentListItem>());
    this.queue$ = this.queueSubject.asObservable();

    this.displayNextAppointmentsSubject = new BehaviorSubject<AppointmentListItem[]>([]);
    this.displayNextAppointments$ = this.displayNextAppointmentsSubject.asObservable();

    this.activeAppointmentItemSubject = new BehaviorSubject<AppointmentListItem>(null);
    this.activeAppointmentItem$ = this.activeAppointmentItemSubject.asObservable();
  }

  set shouldShow(c: boolean) {
    this.shouldShowPopupSubject.next(c);
  }

  get shouldShow(): boolean {
    return this.shouldShowPopupSubject.getValue();
  }

  get queue(): Queue<AppointmentListItem> {
    return this.queueSubject.getValue();
  }

  set queue(c: Queue<AppointmentListItem>) {
    this.queueSubject.next(c);
  }

  addItemsToQueue(items: AppointmentListItem[]): void {
    this.queue.enqueueMany(items);
  }

  set loadingNextConsult(c: boolean) {
    this.loadingNextConsultSubject.next(c);
  }

  get displayNextAppointments(): AppointmentListItem[] {
    return this.displayNextAppointmentsSubject.getValue();
  }

  set displayNextAppointments(c: AppointmentListItem[]) {
    this.displayNextAppointmentsSubject.next(c);
    this.refreshShouldShow();
  }

  get isQueueEmpty(): boolean {
    return this.queue.isEmpty;
  }

  removeQueueItem(appointmentItem: AppointmentListItem): void {
    const index = this.queue.findIndex(e => e.appointment.id === appointmentItem.appointment.id);
    this.queue.remove(index);
  }

  private refreshShouldShow(): void {
    this.shouldShow = hasValue(this.displayNextAppointments);
  }

  /**
   * peek(extract) item from the queue
   * @param numberOfItemsToPeek
   */
  public initialiseNextAppointmentsToDisplay(numberOfItemsToPeek: number) {
    this.displayNextAppointments = this.queue.peekNextItems(numberOfItemsToPeek).reverse();
  }

  get activeAppointmentItem(): AppointmentListItem {
    return this.activeAppointmentItemSubject.getValue();
  }

  set activeAppointmentItem(c: AppointmentListItem) {
    this.activeAppointmentItemSubject.next(c);
  }
}

@Injectable({
  providedIn: 'root'
})
export class ConsultationPopupRibbonService
  extends ConsultationPopupRibbonServiceObservables
  implements OnDestroy, OnInit {
  private readonly LOCAL_TAG = 'ConsultationPopupRibbonService';
  private minToday = new Date();
  private maxToday = new Date();

  // The time in minutes to show upcoming (next) consultation notification
  private minimumConsultationNotifyTime = environment.production ? 5 : 700;

  constructor(
    // private appointmentsService: AppointmentsService,
    private tenantService: TenantService,
    private parentCommsService: ParentCommsService,
  ) {
    super();

    this.minToday.setHours(0, 0, 0, 0);
    this.maxToday.setHours(24, 0, 0, 0);
  }

  ngOnInit(): void {
    // refreshing when the service is initialised
    this.refresh();
  }

  /**
   * Destroys all the instance variables
   */
  ngOnDestroy(): void {
    this.shouldShow = false;
    this.queue = new Queue<AppointmentListItem>();
    this.loadingNextConsult = false;
    this.displayNextAppointments = [];
    this.activeAppointmentItem = null;
  }

  /**
   * Refreshes the consultation popup data
   */
  refresh(): void {
    this.getSetConsultationQueue();
  }

  /**
   * Checks if the appointmentsToday is set or not, if not queries the appointments that the practitioner has for today
   * @private
   */
  private async getSetConsultationQueue(): Promise<void> {
    if (this.isQueueEmpty) {
      // let appointments = await this.appointmentsService.list({
      //   StartTimeMin: this.minToday.toISOString(),
      //   StartTimeMax: this.maxToday.toISOString(),
      //   MaxResultCount: 40,
      //   Status: AppointmentStatus.Confirmed,
      //   Sorting: 'appointment.startTime ASC',
      // });
      //
      // // filtering out all completed appointments
      // appointments = appointments.filter((aListItem) => {
      //   const appt = aListItem.appointment;
      //   return !hasValue(appt?.recordedEndTime);
      // });
      //
      // // add appointments to the queue
      // this.addItemsToQueue(appointments);
      //
      // this.activeAppointmentItem = this.queue.find(e => {
      //   return hasValue(e.appointment.recordedStartTime) && !hasValue(e.appointment.recordedEndTime);
      // });
    }

    // 3 = number of appointments to fetch
    this.initialiseNextAppointmentsToDisplay(3);

  }

  /**
   * Start Consultation
   * @param appointment
   */

  public async startConsultation(appointment: AppointmentListItem): Promise<void> {
    this.loadingNextConsult = true;

    // Stopping currentConsultation (updating the endTime to the current time)
    if (hasValue(this.activeAppointmentItem)) {
      await this.stopActiveConsultation();
    }

    const tenantId = this.tenantService.selectedTenantValue.id;

    // const apiResponse = await this.appointmentsService.markAppointmentStarted(
    //   appointment.appointment.id,
    //   tenantId
    // ).then(r => r.body);

    // Update the display appointments

    // appointment.appointment = apiResponse;

    let updatedAppointmentList = this.displayNextAppointments;

    const index = updatedAppointmentList.findIndex(e => e.appointment.id === appointment.appointment.id);

    // updating the new appointment value from the DB in memory
    updatedAppointmentList[index] = appointment;
    this.displayNextAppointments = updatedAppointmentList;

    // Update Active appointment
    this.activeAppointmentItem = appointment;

    // starting the call if applicable
    this.startCallBasedOnActiveAppointment();

    // stopping the loading indicator
    this.loadingNextConsult = false;
  }

  /**
   * Calls the patient for the appointment:
   * IF it is in progress
   * IF it is audio / video
   * @private
   */
  public startCallBasedOnActiveAppointment() {
    const appointment = this.activeAppointmentItem;
    if (!hasValue(appointment)) {
      return;
    }

    const hasStarted = AppointmentUtil.isAppointmentInProgress(appointment.appointment);

    if (!hasStarted) {
      console.error('Appointment is not in progress...')
      return;
    }

    // hang up previous call
    this.parentCommsService.hangUpCall();

    // set call type (video or audio)
    const apptTypeString = AppointmentType.nameToNormalised(appointment.appointmentType.name);
  }

  /**
   * Stops the currentConsultation, if there currently is no currentConsultation then it does nothing
   */
  public async stopActiveConsultation(): Promise<void> {

    //let activeAppointment: AppointmentListItem = null;
    const activeAppointment = this.activeAppointmentItem;

    if (hasValue(activeAppointment)) {

      // api req.
      // await this.appointmentsService.update(
      //   {recordedEndTime: DateUtil.nowUTC()},
      //   activeAppointment.appointment.id,
      //   activeAppointment.tenantId
      // );

      // TODO update the consult here or else

      // remove item from queue
      this.removeQueueItem(activeAppointment);

      // refresh the display appointments
      this.refresh();
    }
  }
}
